import dayjs from "dayjs";
import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";
import {
  updateHarvestedPlots,
  updateUnHarvestedPlots,
} from "../../../utils/api.js";

/**
 * Select targets from cell meta.
 * - `targets`: UNHARVESTED rows (for options 1–3).
 * - `harvestedTargets`: HARVESTED rows (for Unharvest).
 * - `allHarvested`: true if every row in the cell is harvested.
 */
export function selectTargetsFromCell(meta) {
  const greenhouseName = meta?.greenhouseName;
  const rowStates = Array.isArray(meta?.rowStates) ? meta.rowStates : [];

  const unharvested = rowStates
    .filter((r) => !r.harvested)
    .map((r) => ({
      greenhouse_name: greenhouseName,
      plot_name: r.rowLabel,
    }));

  const harvested = rowStates
    .filter((r) => r.harvested)
    .map((r) => ({
      greenhouse_name: greenhouseName,
      plot_name: r.rowLabel,
    }));

  const allHarvested = rowStates.length > 0 && unharvested.length === 0;
  return {
    targets: unharvested,
    harvestedTargets: harvested,
    hasAnyUnharvested: unharvested.length > 0,
    hasAnyHarvested: harvested.length > 0,
    allHarvested,
  };
}

/**
 * Quick mark: today / yesterday
 * total_yield and total_weight set to 0 (per your Spinach behavior).
 */
export async function markQuick({ kind, targets, org, siteId, cropId }) {
  const base = dayjs();
  const date = kind === "yesterday" ? base.subtract(1, "day") : base;
  const harvestedDate = date.format(BACKEND_DATE_FORMAT);

  const results = await Promise.allSettled(
    targets.map((t) =>
      updateHarvestedPlots(
        org,
        siteId,
        cropId,
        null, // greenhouse_id
        null, // plot_id
        harvestedDate,
        0, // total_yield
        0, // total_weight
        t.greenhouse_name,
        t.plot_name,
      ),
    ),
  );

  const failed = results.filter(
    (r) => r.status === "rejected" || (r.value && r.value.status !== 200),
  ).length;

  return { failed, total: targets.length };
}

/**
 * Custom date modal save.
 * Pallet status -> yield mapping:
 * - normal: crates * tubsPerCrate + tubs
 * - unavailable: 0
 * - discarded: -1
 * Weight is optional; default 0.
 */
export async function markCustom({
  date,
  crates = 0,
  tubs = 0,
  weight = 0,
  palletStatus = "normal",
  tubsPerCrate = 36,
  targets,
  org,
  siteId,
  cropId,
}) {
  const harvestedDate = dayjs(date).format(BACKEND_DATE_FORMAT);

  let totalYield;
  if (palletStatus === "discarded") totalYield = -1;
  else if (palletStatus === "unavailable") totalYield = 0;
  else totalYield = crates * tubsPerCrate + tubs;

  const results = await Promise.allSettled(
    targets.map((t) =>
      updateHarvestedPlots(
        org,
        siteId,
        cropId,
        null, // greenhouse_id
        null, // plot_id
        harvestedDate,
        totalYield,
        weight ?? 0,
        t.greenhouse_name,
        t.plot_name,
      ),
    ),
  );

  const failed = results.filter(
    (r) => r.status === "rejected" || (r.value && r.value.status !== 200),
  ).length;

  return { failed, total: targets.length };
}

/**
 * Unharvest (revert) previously harvested rows.
 * Uses names (greenhouse_name, plot_name) like your other calls.
 */
export async function unharvest({ targets, org, siteId, cropId }) {
  const results = await Promise.allSettled(
    targets.map((t) =>
      updateUnHarvestedPlots(
        org,
        siteId,
        cropId,
        null, // greenhouse_id
        null, // plot_id
        t.greenhouse_name,
        t.plot_name,
      ),
    ),
  );

  const failed = results.filter(
    (r) => r.status === "rejected" || (r.value && r.value.status !== 200),
  ).length;

  return { failed, total: targets.length };
}
